import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { AvatarDisc, Button, Input, MonoLabel, Row } from '@/component';
import { useFriendSearch } from '@/hook';
import { cn } from '@/lib/cn';
import { RelationAction } from './RelationAction';
import { personName } from './personName';

interface FriendSearchProps {
  /** sm is the Home column's 38px field; lg is the Friends header's 44px one. */
  size: 'sm' | 'lg';
  className?: string;
}

/** The permanent "Find friends by @handle" search. Results appear under the
 * field with the button that fits where you stand with each person. */
export function FriendSearch({ size, className }: FriendSearchProps) {
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const matches = useFriendSearch(term);
  const large = size === 'lg';

  function submit(event: FormEvent) {
    event.preventDefault();
    setTerm(draft);
  }

  return (
    <div className={className}>
      <form onSubmit={submit} className={cn('flex', large ? 'gap-[10px]' : 'gap-2')}>
        <Input
          aria-label="Find friends by @handle"
          placeholder="Find friends by @handle"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className={large ? 'h-11 w-[250px] max-w-full text-sm' : 'h-[38px] rounded-[5px] px-3 text-sm'}
        />
        <Button type="submit" variant={large ? 'primary' : 'outline'} size={large ? 'md' : 'sm'} className={large ? 'px-[19px]' : 'px-[14px]'}>
          Search
        </Button>
      </form>
      {term.trim().replace(/^@/, '') && matches && (
        <div className="mt-2">
          {matches.length === 0 ? (
            <p className="py-4 text-sm text-mute">No one with a handle starting @{term.trim().replace(/^@/, '')}.</p>
          ) : (
            matches.map((person) => (
              <Row key={person.userId} className="gap-[14px] py-[14px]">
                <AvatarDisc user={person} size={32} />
                <div className="min-w-0 flex-1">
                  {person.handle ? (
                    <Link to="/u/$handle" params={{ handle: person.handle }} className="block truncate text-sm font-semibold hover:text-acc">
                      {personName(person)}
                    </Link>
                  ) : (
                    <div className="truncate text-sm font-semibold">{personName(person)}</div>
                  )}
                  {person.handle && <MonoLabel className="mt-0.5 block normal-case tracking-normal text-[11px]">@{person.handle}</MonoLabel>}
                </div>
                <RelationAction userId={person.userId} relation={person.relation} />
              </Row>
            ))
          )}
        </div>
      )}
    </div>
  );
}
